import React, { useState, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { jsPDF } from 'jspdf';
import axios from 'axios';
import './CV.css';
import { Document, Packer, Paragraph, TextRun, AlignmentType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';
import Notification from '../components/Notification';
import { useT } from '../context/AppContext';

const TEMPLATES = [
  { id: 'classique', icon: 'fa-align-left', desc: '1 colonne, sobre' },
  { id: 'moderne', icon: 'fa-columns', desc: '2 colonnes, aéré' },
  { id: 'compact', icon: 'fa-compress-alt', desc: 'Dense, tout visible' },
  { id: 'creatif', icon: 'fa-paint-brush', desc: 'Bandeau couleur' },
];

const COLOR_PALETTES = [
  { name: 'Violet', color: '#667eea' },
  { name: 'Bleu', color: '#3b82f6' },
  { name: 'Emeraude', color: '#10b981' },
  { name: 'Corail', color: '#f43f5e' },
  { name: 'Ardoise', color: '#475569' },
  { name: 'Orange', color: '#f97316' },
];

const FONTS = [
  { value: 'Helvetica', label: 'Helvetica (ATS ✓)' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Times', label: 'Times New Roman' },
  { value: 'Courier', label: 'Courier' },
];

const STATUTS_CAND = ['Envoyé', 'Relancé', 'Entretien', 'Test technique', 'Refusé', 'Accepté'];
const STATUT_COLORS = {
  'Envoyé': { bg: '#3b82f620', color: '#3b82f6' },
  'Relancé': { bg: '#f9731620', color: '#f97316' },
  'Entretien': { bg: '#a78bfa20', color: '#a78bfa' },
  'Test technique': { bg: '#eab30820', color: '#d97706' },
  'Refusé': { bg: '#f43f5e20', color: '#f43f5e' },
  'Accepté': { bg: '#10b98120', color: '#10b981' },
};

/* ════════════════════════════════
   STEP BAR
════════════════════════════════ */
const StepBar = ({ step }) => {
  const tr = useT();
  const steps = [
    { n: 1, icon: 'fa-id-card', label: tr('cv_etape1') },
    { n: 2, icon: 'fa-briefcase', label: tr('cv_etape2') },
    { n: 3, icon: 'fa-eye', label: tr('cv_etape3') },
  ];
  return (
    <div className="cv-stepbar">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className={`cv-step ${step === s.n ? 'cv-step-active' : ''} ${step > s.n ? 'cv-step-done' : ''}`}>
            <div className="cv-step-circle">
              {step > s.n ? <i className="fas fa-check"></i> : <i className={`fas ${s.icon}`}></i>}
            </div>
            <span>{s.label}</span>
          </div>
          {i < steps.length - 1 && <div className={`cv-step-line ${step > s.n ? 'cv-step-line-done' : ''}`}></div>}
        </React.Fragment>
      ))}
    </div>
  );
};

/* ════════════════════════════════
   CV PREVIEW
════════════════════════════════ */
const CVPreview = ({ formData, onClose, onDownloadPDF, onDownloadWord }) => {
  const tr = useT();
  const accent = formData.color || '#667eea';
  const tpl = formData.template || 'classique';

  const Section = ({ title, children }) => (
    <div className="cvp-section">
      <h3 className="cvp-section-title" style={{ borderBottomColor: accent, color: accent }}>
        <span>▌</span> {title}
      </h3>
      {children}
    </div>
  );

  const ContactInfo = () => (
    <div className="cvp-contacts">
      {formData.email && <span className="cvp-chip"><i className="fas fa-envelope" style={{ marginRight: '4px', fontSize: '0.7rem' }}></i>{formData.email}</span>}
      {formData.phone && <span className="cvp-chip"><i className="fas fa-phone" style={{ marginRight: '4px', fontSize: '0.7rem' }}></i>{formData.phone}</span>}
      {formData.location && <span className="cvp-chip"><i className="fas fa-map-marker-alt" style={{ marginRight: '4px', fontSize: '0.7rem' }}></i>{formData.location}</span>}
      {formData.linkedin && <span className="cvp-chip cvp-chip-link"><i className="fab fa-linkedin" style={{ marginRight: '4px', fontSize: '0.7rem' }}></i>{formData.linkedin}</span>}
    </div>
  );

  const MainContent = () => (
    <>
      {formData.summary && <Section title={tr('cv_resume')}><p className="cvp-text">{formData.summary}</p></Section>}
      {formData.experiences?.[0]?.title && (
        <Section title={tr('cv_experiences')}>
          {formData.experiences.filter(e => e.title).map((exp, i) => (
            <div key={i} className="cvp-item">
              <div className="cvp-item-header"><strong>{exp.title}</strong><span className="cvp-period">{exp.period}</span></div>
              {exp.company && <div className="cvp-company" style={{ color: accent }}>{exp.company}</div>}
              {exp.description && (
                <ul className="cvp-bullets">
                  {exp.description.split('\n').filter(l => l.trim()).map((line, j) => (
                    <li key={j}>{line.replace(/^[•-]\s*/, '')}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </Section>
      )}
      {formData.education?.[0]?.degree && (
        <Section title={tr('cv_formations')}>
          {formData.education.filter(e => e.degree).map((edu, i) => (
            <div key={i} className="cvp-item">
              <div className="cvp-item-header"><strong>{edu.degree}</strong><span className="cvp-period">{edu.period}</span></div>
              {edu.school && <div className="cvp-company" style={{ color: accent }}>{edu.school}</div>}
            </div>
          ))}
        </Section>
      )}
      {formData.projects?.[0]?.title && (
        <Section title="Projets clés">
          {formData.projects.filter(p => p.title).map((proj, i) => (
            <div key={i} className="cvp-item">
              <div className="cvp-item-header"><strong>{proj.title}</strong><span className="cvp-period">{proj.period}</span></div>
              {proj.technologies && <div className="cvp-company" style={{ color: accent }}>Tech : {proj.technologies}</div>}
              {proj.description && <p className="cvp-text">{proj.description}</p>}
            </div>
          ))}
        </Section>
      )}
    </>
  );

  const SideContent = () => (
    <>
      {(formData.skillsLanguages || formData.skillsFrameworks || formData.skillsTools || formData.skillsMethods) && (
        <Section title={tr('cv_competences')}>
          {[
            { label: 'Langages', val: formData.skillsLanguages },
            { label: 'Frameworks', val: formData.skillsFrameworks },
            { label: 'Outils', val: formData.skillsTools },
            { label: 'Méthodo.', val: formData.skillsMethods },
          ].filter(s => s.val).map((s, i) => (
            <div key={i} className="cvp-skill-row">
              <span className="cvp-skill-label" style={{ color: accent }}>{s.label} :</span>
              <span>{s.val}</span>
            </div>
          ))}
        </Section>
      )}
      {formData.languages && <Section title={tr('cv_langues_cv')}><p className="cvp-text">{formData.languages}</p></Section>}
      {formData.certifications && <Section title="Certifications"><p className="cvp-text">{formData.certifications}</p></Section>}
    </>
  );

  const renderSheet = () => {
    if (tpl === 'classique') return (
      <div className="cvp-sheet" style={{ borderTop: `6px solid ${accent}` }}>
        <div className="cvp-header">
          {formData.photoUrl && formData.showPhoto && <img src={formData.photoUrl} className="cvp-photo" alt="cv" style={{ borderColor: accent }} />}
          <h1 className="cvp-name">{formData.name || 'Votre Nom'}</h1>
          <div className="cvp-title" style={{ color: accent }}>{formData.title || 'Titre professionnel'}</div>
          <ContactInfo />
        </div>
        <div className="cvp-content"><MainContent /><SideContent /></div>
      </div>
    );
    if (tpl === 'moderne') return (
      <div className="cvp-sheet">
        <div className="cvp-header" style={{ borderTop: `6px solid ${accent}`, background: `linear-gradient(135deg,${accent}08,${accent}03)` }}>
          {formData.photoUrl && formData.showPhoto && <img src={formData.photoUrl} className="cvp-photo" alt="cv" style={{ borderColor: accent }} />}
          <h1 className="cvp-name">{formData.name || 'Votre Nom'}</h1>
          <div className="cvp-title" style={{ color: accent }}>{formData.title}</div>
          <ContactInfo />
        </div>
        <div className="cvp-two-col">
          <div className="cvp-col-left" style={{ background: `${accent}10`, borderRight: `2px solid ${accent}25` }}><SideContent /></div>
          <div className="cvp-col-right" style={{ padding: '1.25rem 1.5rem' }}><MainContent /></div>
        </div>
      </div>
    );
    if (tpl === 'compact') return (
      <div className="cvp-sheet" style={{ fontSize: '0.78rem' }}>
        <div style={{ background: `linear-gradient(135deg,${accent},${accent}cc)`, color: '#fff', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {formData.photoUrl && formData.showPhoto && <img src={formData.photoUrl} alt="cv" style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.5)' }} />}
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800 }}>{formData.name || 'Votre Nom'}</h1>
            <div style={{ opacity: 0.9, fontSize: '0.85rem', marginBottom: '0.3rem' }}>{formData.title}</div>
            <div style={{ fontSize: '0.72rem', opacity: 0.85, display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {[formData.email, formData.phone, formData.location].filter(Boolean).map((v, i) => (
                <span key={i} style={{ background: 'rgba(255,255,255,0.2)', padding: '0.1rem 0.5rem', borderRadius: '10px' }}>{v}</span>
              ))}
            </div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 0.7fr' }}>
          <div style={{ padding: '1rem 1.25rem', borderRight: `2px solid ${accent}20` }}><MainContent /></div>
          <div style={{ padding: '1rem', background: `${accent}06` }}><SideContent /></div>
        </div>
      </div>
    );
    if (tpl === 'creatif') return (
      <div className="cvp-sheet">
        <div style={{ background: `linear-gradient(135deg,${accent},${accent}99)`, padding: '1.75rem 2rem', display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          {formData.photoUrl && formData.showPhoto
            ? <img src={formData.photoUrl} className="cvp-photo-creatif" alt="cv" />
            : <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', fontWeight: 800, color: '#fff', flexShrink: 0 }}>{(formData.name || 'V').charAt(0).toUpperCase()}</div>
          }
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: '0 0 0.3rem', fontSize: '1.6rem', fontWeight: 800, color: '#fff' }}>{formData.name || 'Votre Nom'}</h1>
            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem', marginBottom: '0.6rem' }}>{formData.title}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {[formData.email, formData.phone, formData.location].filter(Boolean).map((v, i) => (
                <span key={i} className="cvp-chip cvp-chip-white">{v}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="cvp-two-col">
          <div className="cvp-col-left" style={{ background: `${accent}10`, borderRight: `2px solid ${accent}20` }}><SideContent /></div>
          <div className="cvp-col-right" style={{ padding: '1.25rem 1.5rem' }}><MainContent /></div>
        </div>
      </div>
    );
  };

  return (
    <div className="cvp-overlay" onClick={onClose}>
      <div className="cvp-modal cvp-modal-lg cvp-modal-improved" onClick={e => e.stopPropagation()}>
        <div className="cvp-modal-header-improved">
          <div className="cvp-preview-info">
            <div className="cvp-preview-badge" style={{ background: `${accent}20`, color: accent }}>
              <i className="fas fa-file-alt"></i> {formData.template ? formData.template.charAt(0).toUpperCase() + formData.template.slice(1) : 'Classique'}
            </div>
            <h2 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
              <i className="fas fa-eye" style={{ color: accent, marginRight: '0.5rem' }}></i>
              {tr('cv_etape3')} — {formData.name || 'Votre CV'}
            </h2>
          </div>
          <div className="cvp-modal-actions">
            <button className="btn btn-secondary btn-sm" onClick={onDownloadWord}><i className="fas fa-file-word"></i> Word</button>
            <button className="btn btn-primary btn-sm" onClick={onDownloadPDF} style={{ background: `linear-gradient(135deg,${accent},${accent}cc)` }}><i className="fas fa-file-pdf"></i> PDF</button>
            <button className="cvp-close" onClick={onClose}><i className="fas fa-times"></i></button>
          </div>
        </div>
        <div className="cvp-quick-info">
          <div className="cvp-quick-item"><i className="fas fa-palette" style={{ color: accent }}></i><span>{tr('cv_couleur')} : <strong style={{ color: accent }}>{formData.color}</strong></span></div>
          <div className="cvp-quick-item"><i className="fas fa-font" style={{ color: accent }}></i><span>{tr('cv_police')} : <strong>{formData.font}</strong></span></div>
          <div className="cvp-quick-item"><i className="fas fa-user" style={{ color: accent }}></i><span>{tr('cv_experiences')} : <strong>{(formData.experiences || []).filter(e => e.title).length}</strong></span></div>
          <div className="cvp-quick-item"><i className="fas fa-graduation-cap" style={{ color: accent }}></i><span>{tr('cv_formations')} : <strong>{(formData.education || []).filter(e => e.degree).length}</strong></span></div>
        </div>
        <div className="cvp-body cvp-body-improved">
          <div className="cvp-a4-wrapper">{renderSheet()}</div>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════
   MODALE LETTRE
════════════════════════════════ */
const CoverLetterModal = ({ text, formData, onClose, onDownloadPDF }) => {
  const today = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  // Extrait l'objet du texte généré si présent
  const objetMatch = text.match(/Objet\s*:\s*(.+)/i);
  const objetLine = objetMatch ? objetMatch[1].trim() : 'Candidature spontanée';

  // Corps de la lettre sans les lignes d'en-tête
  const bodyStart = text.indexOf('Madame');
  const body = bodyStart !== -1 ? text.slice(bodyStart) : text;

  return (
    <div className="cvp-overlay" onClick={onClose}>
      <div className="cvp-modal cvp-modal-lg" onClick={e => e.stopPropagation()}>
        <div className="cvp-modal-header">
          <h2><i className="fas fa-envelope-open-text"></i> Lettre de motivation</h2>
          <div className="cvp-modal-actions">
            <button className="btn btn-primary btn-sm" onClick={onDownloadPDF}>
              <i className="fas fa-file-pdf"></i> Télécharger PDF
            </button>
            <button className="cvp-close" onClick={onClose}><i className="fas fa-times"></i></button>
          </div>
        </div>
        <div className="cvp-body" style={{ background: '#e5e7eb', padding: '2rem' }}>
          <div style={{
            background: '#fff', maxWidth: 700, margin: '0 auto',
            padding: '3rem 3.5rem', fontFamily: 'Times New Roman, serif',
            color: '#1a1a1a', boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            fontSize: '0.95rem', lineHeight: 1.8,
          }}>
            {/* Bloc expéditeur */}
            <div style={{ marginBottom: '2.5rem', borderBottom: '1px solid #ddd', paddingBottom: '1.25rem' }}>
              {formData.name && <div style={{ marginBottom: '0.25rem' }}><u>Nom :</u> <strong>{formData.name}</strong></div>}
              {formData.email && <div style={{ marginBottom: '0.25rem' }}><u>Email :</u> {formData.email}</div>}
              {formData.phone && <div style={{ marginBottom: '0.25rem' }}><u>Téléphone :</u> {formData.phone}</div>}
              {formData.location && <div style={{ marginBottom: '0.25rem' }}><u>Adresse :</u> {formData.location}</div>}
              {formData.linkedin && <div style={{ marginBottom: '0.25rem' }}><u>LinkedIn :</u> {formData.linkedin}</div>}
            </div>

            {/* Date et lieu */}
            <div style={{ textAlign: 'right', marginBottom: '2rem', color: '#555', fontSize: '0.9rem' }}>
              {formData.location ? `${formData.location}, ` : ''}{today}
            </div>

            {/* Objet */}
            <div style={{ marginBottom: '2rem' }}>
              <strong><u>Objet :</u></strong> {objetLine}
            </div>

            {/* Corps */}
            <pre style={{
              fontFamily: 'Times New Roman, serif',
              fontSize: '0.95rem', lineHeight: 1.85,
              whiteSpace: 'pre-wrap', margin: 0, color: '#1a1a1a',
            }}>
              {body}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════
   COMPOSANT PRINCIPAL
════════════════════════════════ */
const CV = () => {
  const tr = useT();

  // — Stepper créer CV —
  const [step, setStep] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [showCoverLetter, setShowCoverLetter] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [jobOffer, setJobOffer] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [notif, setNotif] = useState(null);

  // — ATS Score —
  const [atsScore, setAtsScore] = useState(null);
  const [atsLoading, setAtsLoading] = useState(false);

  // — CV sauvegardés —
  const [savedCVs, setSavedCVs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cv_saved_list') || '[]'); } catch { return []; }
  });
  const [showSavedCVs, setShowSavedCVs] = useState(false);
  const [cvSaveName, setCvSaveName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);

  // — Mode tabs —
  const [cvMode, setCvMode] = useState('creer');

  // — Upload CV —
  const [uploadedCV, setUploadedCV] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadJobOffer, setUploadJobOffer] = useState('');
  const [uploadAiLoading, setUploadAiLoading] = useState(false);
  const [uploadAdaptResult, setUploadAdaptResult] = useState(null);
  const [showUploadCoverLetter, setShowUploadCoverLetter] = useState(false);
  const uploadRef = useRef();

  // — Suivi candidatures —
  const [candidatures, setCandidatures] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cv_candidatures') || '[]'); } catch { return []; }
  });
  const [candForm, setCandForm] = useState({ entreprise: '', poste: '', statut: 'Envoyé', date: new Date().toISOString().split('T')[0], notes: '' });
  const [showCandForm, setShowCandForm] = useState(false);
  const [editCandId, setEditCandId] = useState(null);
  const [candSearch, setCandSearch] = useState('');
  const [candFilterStatut, setCandFilterStatut] = useState('tous');

  // — FormData CV —
  const [formData, setFormData] = useState({
    template: 'classique', color: '#667eea', font: 'Helvetica',
    name: '', title: '', email: '', phone: '', location: '', linkedin: '',
    summary: '', showPhoto: false, photoUrl: null,
    experiences: [{ id: 1, title: '', company: '', period: '', description: '' }],
    education: [{ id: 1, degree: '', school: '', period: '', description: '' }],
    projects: [],
    skillsLanguages: '', skillsFrameworks: '', skillsTools: '', skillsMethods: '',
    languages: '', certifications: '', skillsHighlight: '',
  });

  /* ── helpers ── */
  const showNotif = (msg, type = 'success') => { setNotif({ msg, type }); setTimeout(() => setNotif(null), 4000); };
  const update = (field, val) => setFormData(prev => ({ ...prev, [field]: val }));

  const addExp = () => update('experiences', [...formData.experiences, { id: Date.now(), title: '', company: '', period: '', description: '' }]);
  const removeExp = id => update('experiences', formData.experiences.filter(e => e.id !== id));
  const updateExp = (id, f, v) => update('experiences', formData.experiences.map(e => e.id === id ? { ...e, [f]: v } : e));
  const addEdu = () => update('education', [...formData.education, { id: Date.now(), degree: '', school: '', period: '', description: '' }]);
  const removeEdu = id => update('education', formData.education.filter(e => e.id !== id));
  const updateEdu = (id, f, v) => update('education', formData.education.map(e => e.id === id ? { ...e, [f]: v } : e));
  const addProj = () => update('projects', [...(formData.projects || []), { id: Date.now(), title: '', period: '', technologies: '', description: '' }]);
  const removeProj = id => update('projects', formData.projects.filter(p => p.id !== id));
  const updateProj = (id, f, v) => update('projects', formData.projects.map(p => p.id === id ? { ...p, [f]: v } : p));

  const handlePhoto = e => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => update('photoUrl', ev.target.result);
    reader.readAsDataURL(file);
  };

  /* ── Candidatures ── */
  const saveCandidatures = (list) => { setCandidatures(list); localStorage.setItem('cv_candidatures', JSON.stringify(list)); };

  const handleSaveCand = () => {
    if (!candForm.entreprise.trim() || !candForm.poste.trim()) { showNotif('Entreprise et poste obligatoires', 'error'); return; }
    if (editCandId) {
      saveCandidatures(candidatures.map(c => c.id === editCandId ? { ...c, ...candForm } : c));
      showNotif('Candidature mise à jour ✓');
    } else {
      saveCandidatures([...candidatures, { id: Date.now(), ...candForm, createdAt: new Date().toISOString() }]);
      showNotif('Candidature ajoutée ✓');
    }
    setCandForm({ entreprise: '', poste: '', statut: 'Envoyé', date: new Date().toISOString().split('T')[0], notes: '' });
    setShowCandForm(false); setEditCandId(null);
  };

  const handleEditCand = (c) => { setCandForm({ entreprise: c.entreprise, poste: c.poste, statut: c.statut, date: c.date, notes: c.notes || '' }); setEditCandId(c.id); setShowCandForm(true); };
  const handleDeleteCand = (id) => { if (!window.confirm('Supprimer cette candidature ?')) return; saveCandidatures(candidatures.filter(c => c.id !== id)); showNotif('Candidature supprimée'); };
  const filteredCandidatures = candidatures.filter(c => {
    const m = c.entreprise.toLowerCase().includes(candSearch.toLowerCase()) || c.poste.toLowerCase().includes(candSearch.toLowerCase());
    return m && (candFilterStatut === 'tous' || c.statut === candFilterStatut);
  });

  /* ── Upload CV + extraction texte ── */
  const handleCVDrop = e => { e.preventDefault(); setDragOver(false); processUploadedCV(e.dataTransfer.files[0]); };

  const processUploadedCV = async (file) => {
    if (!file) return;
    const allowed = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!allowed.includes(file.type)) {
      showNotif('Format non supporté — PDF ou Word (.docx) uniquement', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showNotif('Fichier trop lourd (max 10 Mo)', 'error');
      return;
    }

    const url = URL.createObjectURL(file);
    let extractedText = '';

    try {
      if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        // DOCX → mammoth
        const mammoth = await import('mammoth');
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        extractedText = result.value;

      } else {
        // PDF → pdfjs-dist
        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        const texts = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          const pageText = content.items.map(item => item.str).join(' ');
          texts.push(pageText);
        }
        extractedText = texts.join('\n');
      }
    } catch (err) {
      console.error('Extraction échouée:', err);
      showNotif('Extraction du texte échouée — vérifiez que le fichier est lisible', 'error');
    }

    setUploadedCV({ name: file.name, size: file.size, type: file.type, url, extractedText });
    setUploadAdaptResult(null);
    setAtsScore(null);

    if (extractedText && extractedText.trim().length > 50) {
      showNotif(`"${file.name}" chargé — ${extractedText.split(/\s+/).length} mots extraits ✓`);
    } else {
      showNotif(`"${file.name}" chargé — extraction limitée, résultats IA peuvent varier`, 'error');
    }
  };

  /* ── IA upload : adapter le CV chargé ── */
  const handleAdaptUploadedCV = async () => {
    if (!uploadJobOffer.trim()) { showNotif("Collez d'abord l'offre d'emploi", 'error'); return; }
    if (!uploadedCV?.extractedText) { showNotif('Aucun texte extrait du CV', 'error'); return; }

    setUploadAiLoading(true);
    try {
      const response = await axios.post('/ai/adapt-cv', {
        cvData: null,
        cvText: uploadedCV.extractedText,
        cvFileName: uploadedCV.name,
        cvType: uploadedCV.type,
        jobOffer: uploadJobOffer,
        mode: 'upload',
      });

      const result = response.data.result;

      if (!result || Object.keys(result).length === 0) {
        showNotif("L'IA n'a pas retourné de résultat — réessayez", 'error');
        return;
      }

      setUploadAdaptResult({
        summary: result.summary || '',
        skills: result.skills || result.skillsHighlight || '',
        coverLetter: result.coverLetter || '',
      });

      showNotif('CV analysé et lettre générée ✨');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Erreur lors de l'analyse";
      showNotif(msg, 'error');
      console.error('handleAdaptUploadedCV:', err);
    } finally {
      setUploadAiLoading(false);
    }
  };

  /* ── Score ATS ── */
  const computeATSScore = async (cvText) => {
    if (!cvText || cvText.startsWith('data:')) {
      showNotif('Extraction texte non disponible pour ce PDF — utilisez un .docx pour le score ATS', 'error');
      return;
    }
    setAtsLoading(true);
    try {
      const res = await axios.post('/ai/ats-score', {
        cvText,
        jobOffer: uploadJobOffer || '',
      });
      setAtsScore(res.data.score);
    } catch {
      // Fallback : score local basé sur le contenu
      const text = cvText.toLowerCase();
      let score = 0;
      const checks = [
        { label: 'Coordonnées présentes', points: 15, ok: /@|tel|téléphone|phone|email/.test(text) },
        { label: 'Résumé / profil', points: 10, ok: /résumé|profil|objectif|summary|about/.test(text) },
        { label: 'Expérience professionnelle', points: 20, ok: /expérience|experience|emploi|poste|job|work/.test(text) },
        { label: 'Formation / diplôme', points: 15, ok: /formation|diplôme|master|licence|bac|bachelor|school|université/.test(text) },
        { label: 'Compétences techniques', points: 15, ok: /compétence|skill|javascript|python|react|sql|java|html|css|node/.test(text) },
        { label: 'Langues', points: 5, ok: /anglais|français|espagnol|language|lang/.test(text) },
        { label: 'Longueur correcte (>300 mots)', points: 10, ok: cvText.split(/\s+/).length > 300 },
        { label: 'Pas de caractères spéciaux', points: 5, ok: (cvText.match(/[^\w\s.,;:!?@()\-'"]/g) || []).length < 20 },
        { label: 'Mots-clés offre détectés', points: 5, ok: uploadJobOffer ? uploadJobOffer.toLowerCase().split(/\s+/).filter(w => w.length > 4).some(w => text.includes(w)) : false },
      ];
      checks.forEach(c => { if (c.ok) score += c.points; });
      setAtsScore({ total: score, checks, fallback: true });
    } finally {
      setAtsLoading(false);
    }
  };

  /* ── Sauvegarder CV ── */
  const handleSaveCV = () => {
    if (!cvSaveName.trim()) { showNotif('Donnez un nom à ce CV', 'error'); return; }
    const entry = {
      id: Date.now(),
      name: cvSaveName.trim(),
      savedAt: new Date().toISOString(),
      data: formData,
    };
    const updated = [entry, ...savedCVs.filter(c => c.name !== cvSaveName.trim())].slice(0, 10);
    setSavedCVs(updated);
    localStorage.setItem('cv_saved_list', JSON.stringify(updated));
    setShowSaveModal(false);
    setCvSaveName('');
    showNotif(`CV "${entry.name}" sauvegardé ✓`);
  };

  const handleLoadCV = (saved) => {
    setFormData(saved.data);
    setShowSavedCVs(false);
    setStep(1);
    setCvMode('creer');
    showNotif(`CV "${saved.name}" chargé ✓`);
  };

  const handleDeleteSavedCV = (id) => {
    const updated = savedCVs.filter(c => c.id !== id);
    setSavedCVs(updated);
    localStorage.setItem('cv_saved_list', JSON.stringify(updated));
    showNotif('CV supprimé');
  };

  /* ── PDF lettre (upload) ── */
  const generateUploadCoverLetterPDF = () => {
    if (!uploadAdaptResult?.coverLetter) return;
    try {
      const doc = new jsPDF();
      const text = uploadAdaptResult.coverLetter || '';
      const date = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
      const fileName = uploadedCV?.name?.replace(/\.[^.]+$/, '') || 'Candidat';

      // En-tête
      doc.setFontSize(12); doc.setFont('times', 'bold');
      doc.text(fileName, 20, 25);
      doc.setFont('times', 'normal'); doc.setFontSize(10);
      doc.text(date, 190, 25, { align: 'right' });

      // Séparateur
      doc.setDrawColor(100, 100, 100);
      doc.line(20, 32, 190, 32);

      // Titre
      let y = 45;
      doc.setFontSize(13); doc.setFont('times', 'bold');
      doc.text('Lettre de motivation', 105, y, { align: 'center' });
      y += 12;

      // Corps — découpe en lignes pour éviter les débordements
      doc.setFontSize(11); doc.setFont('times', 'normal');
      const lines = doc.splitTextToSize(text, 170);
      lines.forEach(line => {
        if (y > 275) { doc.addPage(); y = 20; }
        doc.text(line || '', 20, y);
        y += 6;
      });

      doc.save('Lettre_motivation.pdf');
      showNotif('Lettre téléchargée ✓');
    } catch (err) {
      console.error('Erreur PDF lettre:', err);
      showNotif('Erreur lors de la génération', 'error');
    }
  };

  /* ── IA créer CV ── */
  const handleAdaptCV = async () => {
    if (!jobOffer.trim()) { showNotif(tr('cv_offre_emploi') + ' requis', 'error'); return; }
    setAiLoading(true);
    try {
      const response = await axios.post('/ai/adapt-cv', { cvData: formData, jobOffer });
      const result = response.data.result;
      setFormData(prev => ({ ...prev, summary: result.summary || prev.summary, skillsHighlight: result.skillsHighlight || '' }));
      setCoverLetter(result.coverLetter || '');
      showNotif(tr('cv_analyse_ia') + ' ✨');
    } catch (err) {
      showNotif(err.response?.data?.message || tr('erreur_sauvegarde'), 'error');
    } finally { setAiLoading(false); }
  };

  /* ── Exports ── */
  const hexToRgb = hex => ({ r: parseInt(hex.slice(1, 3), 16), g: parseInt(hex.slice(3, 5), 16), b: parseInt(hex.slice(5, 7), 16) });

  const generatePDF = async () => {
    const doc = new jsPDF();
    const accent = formData.color || '#667eea';
    const rgb = hexToRgb(accent);
    const existing = JSON.parse(localStorage.getItem('appNotifications') || '[]');
    localStorage.setItem('appNotifications', JSON.stringify([{ id: Date.now(), type: 'success', icon: 'fa-file-alt', title: tr('pdf_telecharge'), desc: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), time: "À l'instant", read: false }, ...existing].slice(0, 20)));
    doc.setFillColor(rgb.r, rgb.g, rgb.b); doc.rect(0, 0, 210, 35, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(20); doc.setFont('helvetica', 'bold');
    doc.text(formData.name || 'Nom', 20, 20);
    doc.setFontSize(11); doc.setFont('helvetica', 'normal'); doc.text(formData.title || '', 20, 28);
    doc.setTextColor(0, 0, 0); let y = 45; doc.setFontSize(9);
    const contacts = [formData.email, formData.phone, formData.location].filter(Boolean).join('  |  ');
    if (contacts) { doc.text(contacts, 20, y); y += 8; }
    if (formData.summary) {
      doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(rgb.r, rgb.g, rgb.b);
      doc.text(tr('cv_resume').toUpperCase(), 20, y); y += 5;
      doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
      const lines = doc.splitTextToSize(formData.summary, 170); doc.text(lines, 20, y); y += lines.length * 5 + 5;
    }
    if (formData.experiences?.[0]?.title) {
      doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(rgb.r, rgb.g, rgb.b);
      doc.text(tr('cv_experiences').toUpperCase(), 20, y); y += 5; doc.setTextColor(0, 0, 0);
      formData.experiences.filter(e => e.title).forEach(exp => {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
        doc.text(`${exp.title} - ${exp.company}`, 20, y); doc.setFont('helvetica', 'normal');
        doc.text(exp.period || '', 170, y, { align: 'right' }); y += 5;
        if (exp.description) { const lines = doc.splitTextToSize(exp.description, 165); doc.text(lines, 25, y); y += lines.length * 4 + 3; }
      }); y += 3;
    }
    const skills = [formData.skillsLanguages, formData.skillsFrameworks, formData.skillsTools].filter(Boolean).join(', ');
    if (skills) {
      doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(rgb.r, rgb.g, rgb.b);
      doc.text(tr('cv_competences').toUpperCase(), 20, y); y += 5;
      doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
      doc.text(doc.splitTextToSize(skills, 170), 20, y);
    }
    try { await axios.post('/pdf', { type: 'cv', fileName: `CV_${formData.name || 'cv'}.pdf`, data: { name: formData.name, title: formData.title, template: formData.template } }); } catch (err) { console.error(err.message); }
    doc.save(`CV_${formData.name || 'cv'}.pdf`);
    showNotif(tr('pdf_telecharge'));
  };

  const generateWord = async () => {
    try {
      const colorHex = (formData.color || '#667eea').replace('#', '');
      const makeParagraph = (text, opts = {}) => new Paragraph({ children: [new TextRun({ text: text || '', ...opts })], spacing: { after: 100 } });
      const makeHeading = (text) => new Paragraph({ children: [new TextRun({ text, bold: true, size: 24, color: colorHex })], spacing: { before: 300, after: 100 }, border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: colorHex } } });
      const secs = [];
      secs.push(
        new Paragraph({ children: [new TextRun({ text: formData.name || 'Votre Nom', bold: true, size: 36, color: colorHex })], alignment: AlignmentType.CENTER, spacing: { after: 80 } }),
        new Paragraph({ children: [new TextRun({ text: formData.title || '', size: 24, color: '666666' })], alignment: AlignmentType.CENTER, spacing: { after: 80 } }),
      );
      const contacts = [formData.email, formData.phone, formData.location, formData.linkedin].filter(Boolean).join('  |  ');
      if (contacts) secs.push(new Paragraph({ children: [new TextRun({ text: contacts, size: 18, color: '666666' })], alignment: AlignmentType.CENTER, spacing: { after: 300 } }));
      if (formData.summary) { secs.push(makeHeading(tr('cv_resume').toUpperCase())); secs.push(makeParagraph(formData.summary, { size: 20 })); }
      (formData.experiences || []).filter(e => e.title).forEach((exp, idx) => {
        if (idx === 0) secs.push(makeHeading(tr('cv_experiences').toUpperCase()));
        secs.push(new Paragraph({ children: [new TextRun({ text: exp.title, bold: true, size: 22 }), new TextRun({ text: exp.company ? `  —  ${exp.company}` : '', size: 22, color: colorHex }), new TextRun({ text: exp.period ? `  (${exp.period})` : '', size: 20, color: '888888' })], spacing: { before: 150, after: 60 } }));
        if (exp.description) exp.description.split('\n').filter(l => l.trim()).forEach(line => secs.push(new Paragraph({ children: [new TextRun({ text: `• ${line.replace(/^[•-]\s*/, '')}`, size: 20 })], spacing: { after: 40 }, indent: { left: 300 } })));
      });
      (formData.education || []).filter(e => e.degree).forEach((edu, idx) => {
        if (idx === 0) secs.push(makeHeading(tr('cv_formations').toUpperCase()));
        secs.push(new Paragraph({ children: [new TextRun({ text: edu.degree, bold: true, size: 22 }), new TextRun({ text: edu.school ? `  —  ${edu.school}` : '', size: 22, color: colorHex }), new TextRun({ text: edu.period ? `  (${edu.period})` : '', size: 20, color: '888888' })], spacing: { before: 150, after: 60 } }));
      });
      const skillsList = [formData.skillsLanguages && `Langages : ${formData.skillsLanguages}`, formData.skillsFrameworks && `Frameworks : ${formData.skillsFrameworks}`, formData.skillsTools && `Outils : ${formData.skillsTools}`, formData.skillsMethods && `Méthodologies : ${formData.skillsMethods}`].filter(Boolean);
      if (skillsList.length > 0) { secs.push(makeHeading(tr('cv_competences').toUpperCase())); skillsList.forEach(s => secs.push(makeParagraph(s, { size: 20 }))); }
      if (formData.languages || formData.certifications) {
        secs.push(makeHeading(`${tr('cv_langues_cv').toUpperCase()} & CERTIFICATIONS`));
        if (formData.languages) secs.push(makeParagraph(`${tr('cv_langues_cv')} : ${formData.languages}`, { size: 20 }));
        if (formData.certifications) secs.push(makeParagraph(`Certifications : ${formData.certifications}`, { size: 20 }));
      }
      const doc = new Document({ sections: [{ properties: {}, children: secs }] });
      saveAs(await Packer.toBlob(doc), `CV_${formData.name || 'cv'}.docx`);
      showNotif(tr('cv_telecharger_word'));
    } catch (err) { console.error(err); showNotif(tr('erreur_sauvegarde'), 'error'); }
  };

  const generateCoverLetterPDF = () => {
    if (!coverLetter) return;
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const today = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    const objetMatch = coverLetter.match(/Objet\s*:\s*(.+)/i);
    const objetLine = objetMatch ? objetMatch[1].trim() : 'Candidature';
    const bodyStart = coverLetter.indexOf('Madame');
    const body = bodyStart !== -1 ? coverLetter.slice(bodyStart) : coverLetter;

    // Marges
    const mL = 25, mR = 25, mT = 25;
    const pageW = 210;
    const textW = pageW - mL - mR;
    let y = mT;

    doc.setFont('times', 'normal');

    // ── Bloc expéditeur ──
    const infos = [
      formData.name ? `Nom : ${formData.name}` : null,
      formData.email ? `Email : ${formData.email}` : null,
      formData.phone ? `Téléphone : ${formData.phone}` : null,
      formData.location ? `Adresse : ${formData.location}` : null,
      formData.linkedin ? `LinkedIn : ${formData.linkedin}` : null,
    ].filter(Boolean);

    doc.setFontSize(11);
    infos.forEach(line => {
      // Soulignement du label
      const colonIdx = line.indexOf(' :');
      const label = line.slice(0, colonIdx + 2);
      const value = line.slice(colonIdx + 2);
      doc.setFont('times', 'normal');
      const labelW = doc.getTextWidth(label);
      doc.text(label, mL, y);
      doc.line(mL, y + 0.5, mL + labelW, y + 0.5); // soulignement
      doc.text(value, mL + labelW, y);
      y += 7;
    });

    // Séparateur
    y += 3;
    doc.setDrawColor(200, 200, 200);
    doc.line(mL, y, pageW - mR, y);
    y += 8;

    // ── Date ──
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    const dateStr = (formData.location ? `${formData.location}, ` : '') + today;
    doc.text(dateStr, pageW - mR, y, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    y += 12;

    // ── Objet ──
    doc.setFontSize(11); doc.setFont('times', 'bold');
    const objetLabel = 'Objet : ';
    const objetLabelW = doc.getTextWidth(objetLabel);
    doc.text(objetLabel, mL, y);
    doc.line(mL, y + 0.5, mL + objetLabelW, y + 0.5);
    doc.setFont('times', 'normal');
    const objetLines = doc.splitTextToSize(objetLine, textW - objetLabelW);
    doc.text(objetLines, mL + objetLabelW, y);
    y += 6 * objetLines.length + 10;

    // ── Corps lettre ──
    doc.setFontSize(11); doc.setFont('times', 'normal');
    const bodyLines = doc.splitTextToSize(body, textW);

    bodyLines.forEach(line => {
      if (y > 270) { doc.addPage(); y = mT; }
      doc.text(line, mL, y);
      y += 6.5;
    });

    doc.save(`Lettre_motivation_${formData.name || 'CV'}.pdf`);
    showNotif('Lettre téléchargée ✓');
  };

  /* ════════════════════════════════
     RENDER
  ════════════════════════════════ */
  return (
    <div className="page-with-sidebar">
      <Sidebar />
      {notif && <Notification type={notif.type} message={notif.msg} onClose={() => setNotif(null)} />}

      <main className="cv-main" style={{ marginLeft: 'var(--sidebar-width)', width: 'calc(100vw - var(--sidebar-width))', minHeight: '100vh', background: 'var(--bg-page)', boxSizing: 'border-box', overflow: 'hidden' }}>

        <div className="cv-header">
          <div>
            <h1><i className="fas fa-file-alt"></i> {tr('cv_titre')}</h1>
            <p>Créez, adaptez et gérez vos CV professionnels</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {savedCVs.length > 0 && (
              <button className="btn btn-secondary btn-sm" onClick={() => setShowSavedCVs(true)}>
                <i className="fas fa-folder-open"></i> Mes CV ({savedCVs.length})
              </button>
            )}
            {cvMode === 'creer' && (
              <button className="btn btn-secondary btn-sm" onClick={() => { setCvSaveName(formData.name || ''); setShowSaveModal(true); }}>
                <i className="fas fa-save"></i> Sauvegarder
              </button>
            )}
          </div>
        </div>

        {/* TABS */}
        <div className="inv-tabs" style={{ marginBottom: 0 }}>
          {[
            { id: 'creer', icon: 'fa-magic', label: 'Créer mon CV' },
            { id: 'upload', icon: 'fa-cloud-upload-alt', label: 'Uploader un CV' },
            { id: 'suivi', icon: 'fa-tasks', label: `Suivi candidatures${candidatures.length > 0 ? ` (${candidatures.length})` : ''}` },
          ].map(tab => (
            <button key={tab.id} className={`inv-tab ${cvMode === tab.id ? 'inv-tab-active' : ''}`} onClick={() => setCvMode(tab.id)}>
              <i className={`fas ${tab.icon}`}></i> {tab.label}
            </button>
          ))}
        </div>

        {/* ══════════ UPLOAD ══════════ */}
        {cvMode === 'upload' && (
          <div className="cv-container">
            <div className="cv-form">
              <div className="card">

                {/* Zone de dépôt */}
                <div className="form-section">
                  <h3><i className="fas fa-cloud-upload-alt"></i> Déposer votre CV existant</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                    Formats acceptés : <strong>PDF</strong> ou <strong>Word (.docx)</strong> — max 10 Mo
                  </p>
                  <div
                    className={`cv-upload-zone ${dragOver ? 'cv-upload-zone-active' : ''} ${uploadedCV ? 'cv-upload-zone-done' : ''}`}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleCVDrop}
                    onClick={() => !uploadedCV && uploadRef.current?.click()}
                  >
                    <input ref={uploadRef} type="file" accept=".pdf,.docx" style={{ display: 'none' }} onChange={e => processUploadedCV(e.target.files[0])} />
                    {uploadedCV ? (
                      <div className="cv-upload-success">
                        <i className={`fas ${uploadedCV.type === 'application/pdf' ? 'fa-file-pdf' : 'fa-file-word'}`}
                          style={{ fontSize: '2.5rem', color: uploadedCV.type === 'application/pdf' ? '#f43f5e' : '#3b82f6', flexShrink: 0 }} />
                        <div className="cv-upload-info">
                          <strong>{uploadedCV.name}</strong>
                          <span>{(uploadedCV.size / 1024).toFixed(0)} Ko</span>
                          {uploadedCV.extractedText && !uploadedCV.extractedText.startsWith('data:') && (
                            <span style={{ color: '#10b981', fontSize: '0.78rem' }}>
                              <i className="fas fa-check-circle"></i> Texte extrait ({uploadedCV.extractedText.split(/\s+/).length} mots)
                            </span>
                          )}
                          {uploadedCV.extractedText?.startsWith('data:') && (
                            <span style={{ color: '#f97316', fontSize: '0.78rem' }}>
                              <i className="fas fa-exclamation-triangle"></i> PDF — texte extrait côté serveur
                            </span>
                          )}
                        </div>
                        <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); setUploadedCV(null); setUploadAdaptResult(null); setAtsScore(null); }}>
                          <i className="fas fa-times"></i> Changer
                        </button>
                      </div>
                    ) : (
                      <>
                        <i className="fas fa-cloud-upload-alt" style={{ fontSize: '3rem', color: 'var(--accent-start)', marginBottom: '1rem', opacity: dragOver ? 1 : 0.5 }}></i>
                        <strong style={{ fontSize: '1rem', color: 'var(--text-primary)' }}>{dragOver ? 'Relâchez pour déposer' : 'Glissez-déposez votre CV ici'}</strong>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.4rem' }}>ou cliquez pour parcourir vos fichiers</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Score ATS */}
                {uploadedCV && (
                  <div className="form-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <h3 style={{ margin: 0 }}>
                        <i className="fas fa-chart-bar" style={{ color: '#f97316' }}></i> Score ATS
                      </h3>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => computeATSScore(uploadedCV.extractedText)}
                        disabled={atsLoading}
                      >
                        {atsLoading
                          ? <><i className="fas fa-spinner fa-spin"></i> Analyse...</>
                          : <><i className="fas fa-search"></i> Analyser mon CV</>
                        }
                      </button>
                    </div>

                    {atsScore && (
                      <div style={{ background: 'var(--bg-subtle)', borderRadius: 'var(--border-radius-lg)', padding: '1.25rem', border: '1px solid var(--border)' }}>
                        {/* Jauge circulaire */}
                        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                          <div style={{ position: 'relative', width: 100, height: 100, flexShrink: 0 }}>
                            <svg width="100" height="100" viewBox="0 0 100 100">
                              <circle cx="50" cy="50" r="40" fill="none" stroke="var(--border)" strokeWidth="10" />
                              <circle
                                cx="50" cy="50" r="40"
                                fill="none"
                                stroke={atsScore.total >= 75 ? '#10b981' : atsScore.total >= 50 ? '#f97316' : '#f43f5e'}
                                strokeWidth="10"
                                strokeLinecap="round"
                                strokeDasharray={`${2 * Math.PI * 40 * atsScore.total / 100} ${2 * Math.PI * 40}`}
                                transform="rotate(-90 50 50)"
                              />
                            </svg>
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontSize: '1.4rem', fontWeight: 800, color: atsScore.total >= 75 ? '#10b981' : atsScore.total >= 50 ? '#f97316' : '#f43f5e' }}>
                                {atsScore.total}
                              </span>
                              <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>/100</span>
                            </div>
                          </div>

                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-primary)', marginBottom: '0.3rem' }}>
                              {atsScore.total >= 75 ? '✅ Excellent — ATS compatible' : atsScore.total >= 50 ? '⚠️ Correct — peut être amélioré' : '❌ Faible — optimisation nécessaire'}
                            </div>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
                              {atsScore.total >= 75
                                ? 'Votre CV passera la plupart des filtres ATS.'
                                : atsScore.total >= 50
                                  ? 'Quelques optimisations peuvent améliorer votre score.'
                                  : 'Votre CV manque d\'éléments importants pour les ATS.'}
                            </p>
                            {atsScore.fallback && (
                              <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', margin: '0.3rem 0 0', fontStyle: 'italic' }}>
                                <i className="fas fa-info-circle"></i> Score calculé localement
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Détail des critères */}
                        {atsScore.checks && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {atsScore.checks.map((c, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.83rem' }}>
                                <i className={`fas ${c.ok ? 'fa-check-circle' : 'fa-times-circle'}`} style={{ color: c.ok ? '#10b981' : '#f43f5e', flexShrink: 0 }}></i>
                                <span style={{ flex: 1, color: 'var(--text-primary)' }}>{c.label}</span>
                                <span style={{
                                  fontSize: '0.72rem', fontWeight: 700,
                                  padding: '0.1rem 0.5rem', borderRadius: '10px',
                                  background: c.ok ? 'rgba(16,185,129,0.1)' : 'rgba(244,63,94,0.1)',
                                  color: c.ok ? '#10b981' : '#f43f5e',
                                }}>
                                  {c.ok ? `+${c.points}` : '0'} pts
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Informations extraites du CV */}
                {uploadedCV && (
                  <div className="form-section">
                    <h3 style={{ marginBottom: '1rem' }}>
                      <i className="fas fa-user" style={{ color: 'var(--accent-start)' }}></i> Informations extraites
                      <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                        vérifiez et corrigez si besoin
                      </span>
                    </h3>
                    <div className="form-row">
                      {[
                        { label: 'Nom complet', key: 'name', placeholder: 'Jean Dupont' },
                        { label: 'Email', key: 'email', placeholder: 'jean@email.com' },
                        { label: 'Téléphone', key: 'phone', placeholder: '+33 6 00 00 00 00' },
                        { label: 'Ville', key: 'location', placeholder: 'Paris, France' },
                        { label: 'LinkedIn', key: 'linkedin', placeholder: 'linkedin.com/in/...' },
                      ].map(field => (
                        <div key={field.key} className="form-group">
                          <label className="form-label">{field.label}</label>
                          <input
                            className="form-input"
                            value={uploadedCV[field.key] || ''}
                            onChange={e => setUploadedCV(p => ({ ...p, [field.key]: e.target.value }))}
                            placeholder={field.placeholder}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* IA adaptation */}
                {uploadedCV && (
                  <div className="form-section" style={{ borderTop: '2px solid var(--border)', paddingTop: '1.5rem' }}>
                    <h3><i className="fas fa-magic" style={{ color: 'var(--accent-start)' }}></i> Adapter ce CV à une offre d'emploi</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginBottom: '1rem' }}>
                      L'IA va lire votre CV, l'adapter à l'offre et générer une lettre de motivation personnalisée.
                    </p>
                    <div className="form-group">
                      <label className="form-label"><i className="fas fa-briefcase"></i> {tr('cv_offre_emploi')}</label>
                      <textarea
                        className="form-textarea" rows={7}
                        value={uploadJobOffer}
                        onChange={e => setUploadJobOffer(e.target.value)}
                        placeholder="Collez ici le texte complet de l'offre d'emploi..."
                      />
                    </div>
                    <button
                      className="btn btn-primary cv-ai-btn"
                      onClick={handleAdaptUploadedCV}
                      disabled={uploadAiLoading || !uploadJobOffer.trim()}
                    >
                      {uploadAiLoading
                        ? <><i className="fas fa-spinner fa-spin"></i> Analyse en cours...</>
                        : <><i className="fas fa-magic"></i> Analyser et adapter</>
                      }
                    </button>
                  </div>
                )}

                {/* Résultats */}
                {uploadAdaptResult && (
                  <div className="form-section cv-ai-results" style={{ borderTop: '2px solid #10b981', paddingTop: '1.5rem' }}>
                    <h3><i className="fas fa-check-circle" style={{ color: '#10b981' }}></i> Analyse terminée ✨</h3>

                    {uploadAdaptResult.summary && (
                      <div className="cv-ai-result-block">
                        <label className="form-label">
                          <i className="fas fa-align-left" style={{ color: 'var(--accent-start)' }}></i> Résumé professionnel optimisé
                        </label>
                        <textarea
                          className="form-textarea" rows={4}
                          value={uploadAdaptResult.summary}
                          onChange={e => setUploadAdaptResult(p => ({ ...p, summary: e.target.value }))}
                        />
                      </div>
                    )}

                    {uploadAdaptResult.skills && (
                      <div className="cv-ai-result-block">
                        <label className="form-label">
                          <i className="fas fa-star" style={{ color: '#f97316' }}></i> Compétences clés pour ce poste
                        </label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.4rem' }}>
                          {uploadAdaptResult.skills.split(',').map((s, i) => (
                            <span key={i} style={{
                              padding: '0.3rem 0.8rem', borderRadius: '20px', fontSize: '0.82rem',
                              fontWeight: 600, background: 'rgba(102,126,234,0.1)', color: 'var(--accent-start)',
                              border: '1px solid rgba(102,126,234,0.2)',
                            }}>
                              {s.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {uploadAdaptResult.coverLetter && (
                      <div className="cv-ai-result-block">
                        <label className="form-label">
                          <i className="fas fa-envelope-open-text" style={{ color: '#10b981' }}></i> Lettre de motivation générée
                        </label>
                        <div style={{
                          background: 'var(--bg-subtle)', border: '1px solid var(--border)',
                          borderLeft: '3px solid #10b981', borderRadius: '10px', padding: '1.25rem',
                          fontSize: '0.88rem', lineHeight: '1.7', color: 'var(--text-primary)',
                          whiteSpace: 'pre-wrap', maxHeight: 320, overflowY: 'auto',
                          fontFamily: 'Georgia, Times New Roman, serif',
                        }}>
                          {uploadAdaptResult.coverLetter}
                        </div>
                        <div className="cv-cover-actions" style={{ marginTop: '0.75rem' }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => setShowUploadCoverLetter(true)}>
                            <i className="fas fa-eye"></i> Voir en plein écran
                          </button>
                          <button className="btn btn-primary btn-sm" onClick={generateUploadCoverLetterPDF}>
                            <i className="fas fa-file-pdf"></i> Télécharger PDF
                          </button>
                        </div>
                      </div>
                    )}

                    {!uploadAdaptResult.coverLetter && !uploadAdaptResult.summary && (
                      <div style={{ color: 'var(--text-secondary)', padding: '1rem', textAlign: 'center', fontSize: '0.88rem' }}>
                        <i className="fas fa-exclamation-circle" style={{ marginRight: '0.4rem', color: '#f97316' }}></i>
                        Résultats vides — réessayez ou vérifiez que le CV contient du texte lisible.
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                      {uploadedCV?.type === 'application/pdf' && (
                        <a href={uploadedCV.url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                          <i className="fas fa-eye"></i> Voir CV original
                        </a>
                      )}
                      {uploadedCV?.url && (
                        <a href={uploadedCV.url} download={uploadedCV.name} className="btn btn-secondary btn-sm">
                          <i className="fas fa-download"></i> Télécharger CV original
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {showUploadCoverLetter && uploadAdaptResult?.coverLetter && (
              <CoverLetterModal
                text={uploadAdaptResult.coverLetter}
                formData={{ name: uploadedCV.name.replace(/\.[^.]+$/, ''), location: '', phone: '', email: '' }}
                onClose={() => setShowUploadCoverLetter(false)}
                onDownloadPDF={generateUploadCoverLetterPDF}
              />
            )}
          </div>
        )}

        {/* ══════════ SUIVI CANDIDATURES ══════════ */}
        {cvMode === 'suivi' && (
          <div className="cv-container" style={{ maxWidth: '1100px' }}>

            {/* Toolbar */}
            <div className="cand-toolbar">
              <div className="cand-search-wrap">
                <i className="fas fa-search"></i>
                <input
                  className="cand-search"
                  placeholder="Rechercher entreprise ou poste..."
                  value={candSearch}
                  onChange={e => setCandSearch(e.target.value)}
                />
              </div>
              <div className="cand-filter">
                <button
                  className={`cand-filter-btn ${candFilterStatut === 'tous' ? 'active' : ''}`}
                  style={candFilterStatut === 'tous' ? { background: 'var(--accent-start)' } : {}}
                  onClick={() => setCandFilterStatut('tous')}
                >
                  Tous
                </button>
                {STATUTS_CAND.map(s => {
                  const colors = { Envoyé: '#3b82f6', Relancé: '#f97316', Entretien: '#a78bfa', 'Test technique': '#d97706', Refusé: '#f43f5e', Accepté: '#10b981' };
                  return (
                    <button
                      key={s}
                      className={`cand-filter-btn ${candFilterStatut === s ? 'active' : ''}`}
                      style={candFilterStatut === s ? { background: colors[s] } : {}}
                      onClick={() => setCandFilterStatut(candFilterStatut === s ? 'tous' : s)}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
              <button className="btn btn-primary" onClick={() => {
                setShowCandForm(true); setEditCandId(null);
                setCandForm({ entreprise: '', poste: '', statut: 'Envoyé', date: new Date().toISOString().split('T')[0], notes: '' });
              }}>
                <i className="fas fa-plus"></i> Nouvelle candidature
              </button>
            </div>

            {/* Stats */}
            <div className="cand-stats">
              {[
                { label: 'Total', count: candidatures.length, color: '#667eea', bg: 'rgba(102,126,234,0.1)', icon: 'fa-paper-plane' },
                { label: 'En cours', count: candidatures.filter(c => !['Refusé', 'Accepté'].includes(c.statut)).length, color: '#f97316', bg: 'rgba(249,115,22,0.1)', icon: 'fa-spinner' },
                { label: 'Acceptés', count: candidatures.filter(c => c.statut === 'Accepté').length, color: '#10b981', bg: 'rgba(16,185,129,0.1)', icon: 'fa-check-circle' },
                { label: 'Refusés', count: candidatures.filter(c => c.statut === 'Refusé').length, color: '#f43f5e', bg: 'rgba(244,63,94,0.1)', icon: 'fa-times-circle' },
              ].map(s => (
                <div key={s.label} className="cand-stat-card">
                  <div className="cand-stat-icon" style={{ background: s.bg, color: s.color }}>
                    <i className={`fas ${s.icon}`}></i>
                  </div>
                  <div>
                    <span className="cand-stat-num" style={{ color: s.color }}>{s.count}</span>
                    <span className="cand-stat-label">{s.label}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Formulaire */}
            {showCandForm && (
              <div className="cand-form-card">
                <h3 style={{ margin: '0 0 1.25rem', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <i className={`fas ${editCandId ? 'fa-edit' : 'fa-plus-circle'}`} style={{ color: 'var(--accent-start)' }}></i>
                  {editCandId ? 'Modifier la candidature' : 'Nouvelle candidature'}
                </h3>
                <div className="form-row" style={{ marginBottom: '1rem' }}>
                  <div className="form-group"><label className="form-label">Entreprise *</label><input className="form-input" placeholder="Google, SNCF..." value={candForm.entreprise} onChange={e => setCandForm(p => ({ ...p, entreprise: e.target.value }))} autoFocus /></div>
                  <div className="form-group"><label className="form-label">Poste *</label><input className="form-input" placeholder="Développeur Full Stack" value={candForm.poste} onChange={e => setCandForm(p => ({ ...p, poste: e.target.value }))} /></div>
                  <div className="form-group"><label className="form-label">Statut</label>
                    <select className="form-select" value={candForm.statut} onChange={e => setCandForm(p => ({ ...p, statut: e.target.value }))}>
                      {STATUTS_CAND.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Date</label><input type="date" className="form-input" value={candForm.date} onChange={e => setCandForm(p => ({ ...p, date: e.target.value }))} /></div>
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} style={{ minHeight: '70px' }} placeholder="Contact RH, lien de l'offre, remarques..." value={candForm.notes} onChange={e => setCandForm(p => ({ ...p, notes: e.target.value }))} /></div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button className="btn btn-primary" onClick={handleSaveCand}><i className="fas fa-save"></i> {editCandId ? 'Mettre à jour' : 'Enregistrer'}</button>
                  <button className="btn btn-secondary" onClick={() => { setShowCandForm(false); setEditCandId(null); }}><i className="fas fa-times"></i> Annuler</button>
                </div>
              </div>
            )}

            {/* Table */}
            {filteredCandidatures.length === 0 ? (
              <div className="cand-empty">
                <i className="fas fa-inbox"></i>
                <p>{candidatures.length === 0 ? 'Aucune candidature enregistrée. Cliquez sur "Nouvelle candidature" pour commencer.' : 'Aucun résultat pour cette recherche.'}</p>
              </div>
            ) : (
              <div className="cand-table-wrapper">
                <table className="cand-table">
                  <thead>
                    <tr>
                      <th>Entreprise</th>
                      <th>Poste</th>
                      <th>Statut</th>
                      <th>Date</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCandidatures.map(c => {
                      const sc = STATUT_COLORS[c.statut] || { bg: '#66666620', color: '#666' };
                      return (
                        <tr key={c.id}>
                          <td className="cand-enterprise">{c.entreprise}</td>
                          <td className="cand-poste">{c.poste}</td>
                          <td><span className="cand-badge" style={{ background: sc.bg, color: sc.color }}>{c.statut}</span></td>
                          <td className="cand-date">{c.date ? new Date(c.date).toLocaleDateString('fr-FR') : '—'}</td>
                          <td className="cand-notes">{c.notes || <em style={{ opacity: 0.4 }}>—</em>}</td>
                          <td>
                            <div className="cand-actions-cell">
                              <button className="cand-action-btn" onClick={() => handleEditCand(c)} title="Modifier"><i className="fas fa-edit"></i></button>
                              <button className="cand-action-btn danger" onClick={() => handleDeleteCand(c.id)} title="Supprimer"><i className="fas fa-trash"></i></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ══════════ CRÉER CV ══════════ */}
        {cvMode === 'creer' && (
          <>
            <StepBar step={step} />

            {/* ÉTAPE 1 */}
            {step === 1 && (
              <div className="cv-container">
                <div className="cv-form">
                  <div className="card">
                    <div className="form-section">
                      <h3><i className="fas fa-palette"></i> {tr('cv_template')} & {tr('cv_couleur')}</h3>
                      <div className="cv-templates-grid">
                        {TEMPLATES.map(t => (
                          <div key={t.id} className={`cv-tpl-card ${formData.template === t.id ? 'cv-tpl-active' : ''}`} onClick={() => update('template', t.id)}>
                            <i className={`fas ${t.icon}`}></i>
                            <strong>{t.id.charAt(0).toUpperCase() + t.id.slice(1)}</strong>
                            <span>{t.desc}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop: '1rem' }}>
                        <label className="form-label">{tr('cv_couleur')} principale</label>
                        <div className="cv-palette">
                          {COLOR_PALETTES.map(p => (
                            <button key={p.color} className={`cv-swatch ${formData.color === p.color ? 'cv-swatch-active' : ''}`} style={{ background: p.color }} title={p.name} onClick={() => update('color', p.color)} />
                          ))}
                          <input type="color" className="cv-custom-color" value={formData.color} onChange={e => update('color', e.target.value)} />
                        </div>
                      </div>
                      <div className="form-group" style={{ marginTop: '1rem', maxWidth: '250px' }}>
                        <label className="form-label">{tr('cv_police')}</label>
                        <select className="form-select" value={formData.font} onChange={e => update('font', e.target.value)}>
                          {FONTS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="form-section">
                      <h3><i className="fas fa-user"></i> {tr('cv_infos_perso')}</h3>
                      <div className="cv-photo-row" style={{ marginBottom: '1.5rem' }}>
                        <label className="cv-photo-toggle">
                          <input type="checkbox" checked={formData.showPhoto} onChange={e => update('showPhoto', e.target.checked)} />
                          <span className="cv-toggle-track"><span className="cv-toggle-thumb"></span></span>
                          Ajouter une photo
                        </label>
                        {formData.showPhoto && (
                          <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {formData.photoUrl ? <img src={formData.photoUrl} className="cvp-photo-preview" alt="apercu" /> : <span className="btn btn-secondary btn-sm"><i className="fas fa-upload"></i> Choisir</span>}
                            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhoto} />
                          </label>
                        )}
                      </div>
                      <div className="form-row">
                        {[
                          { label: `${tr('cv_nom')} *`, field: 'name', placeholder: 'Jean Dupont' },
                          { label: tr('cv_poste'), field: 'title', placeholder: 'Développeur Full Stack' },
                          { label: tr('email'), field: 'email', placeholder: 'jean@email.com', type: 'email' },
                          { label: tr('cv_tel'), field: 'phone', placeholder: '+33 6 00 00 00 00' },
                          { label: 'Localisation', field: 'location', placeholder: 'Paris, France' },
                          { label: tr('cv_linkedin'), field: 'linkedin', placeholder: 'linkedin.com/in/jean' },
                        ].map(({ label, field, placeholder, type = 'text' }) => (
                          <div key={field} className="form-group">
                            <label className="form-label">{label}</label>
                            <input type={type} className="form-input" placeholder={placeholder} value={formData[field]} onChange={e => update(field, e.target.value)} />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="form-section">
                      <h3><i className="fas fa-align-left"></i> {tr('cv_resume')}</h3>
                      <div className="form-group">
                        <label className="form-label">{tr('cv_resume')}</label>
                        <textarea className="form-textarea" rows={4} placeholder="Développeur passionné avec 5 ans d'expérience..." value={formData.summary} onChange={e => update('summary', e.target.value)} />
                      </div>
                    </div>

                    <div className="form-section">
                      <h3><i className="fas fa-briefcase"></i> {tr('cv_experiences')}</h3>
                      {formData.experiences.map((exp, idx) => (
                        <div key={exp.id} className="dynamic-item">
                          <div className="item-header">
                            <span className="item-number">{tr('cv_experiences')} #{idx + 1}</span>
                            {formData.experiences.length > 1 && <button className="btn-remove" onClick={() => removeExp(exp.id)}><i className="fas fa-trash"></i> {tr('supprimer')}</button>}
                          </div>
                          <div className="form-row">
                            <div className="form-group"><label className="form-label">Poste *</label><input className="form-input" placeholder="Développeur React" value={exp.title} onChange={e => updateExp(exp.id, 'title', e.target.value)} /></div>
                            <div className="form-group"><label className="form-label">Entreprise</label><input className="form-input" placeholder="FlowKraft Agency" value={exp.company} onChange={e => updateExp(exp.id, 'company', e.target.value)} /></div>
                            <div className="form-group"><label className="form-label">Période</label><input className="form-input" placeholder="Jan 2022 – Présent" value={exp.period} onChange={e => updateExp(exp.id, 'period', e.target.value)} /></div>
                          </div>
                          <div className="form-group">
                            <label className="form-label">{tr('description')} (une mission par ligne)</label>
                            <textarea className="form-textarea" rows={3} value={exp.description} onChange={e => updateExp(exp.id, 'description', e.target.value)} />
                          </div>
                        </div>
                      ))}
                      <button className="btn btn-secondary btn-sm" onClick={addExp}><i className="fas fa-plus"></i> {tr('cv_ajouter_exp')}</button>
                    </div>

                    <div className="form-section">
                      <h3><i className="fas fa-graduation-cap"></i> {tr('cv_formations')}</h3>
                      {formData.education.map((edu, idx) => (
                        <div key={edu.id} className="dynamic-item">
                          <div className="item-header">
                            <span className="item-number">{tr('cv_formations')} #{idx + 1}</span>
                            {formData.education.length > 1 && <button className="btn-remove" onClick={() => removeEdu(edu.id)}><i className="fas fa-trash"></i> {tr('supprimer')}</button>}
                          </div>
                          <div className="form-row">
                            <div className="form-group"><label className="form-label">Diplôme *</label><input className="form-input" placeholder="Master Informatique" value={edu.degree} onChange={e => updateEdu(edu.id, 'degree', e.target.value)} /></div>
                            <div className="form-group"><label className="form-label">École</label><input className="form-input" placeholder="Université Paris" value={edu.school} onChange={e => updateEdu(edu.id, 'school', e.target.value)} /></div>
                            <div className="form-group"><label className="form-label">Période</label><input className="form-input" placeholder="2018 – 2020" value={edu.period} onChange={e => updateEdu(edu.id, 'period', e.target.value)} /></div>
                          </div>
                        </div>
                      ))}
                      <button className="btn btn-secondary btn-sm" onClick={addEdu}><i className="fas fa-plus"></i> {tr('cv_ajouter_edu')}</button>
                    </div>

                    <div className="form-section">
                      <h3><i className="fas fa-code"></i> {tr('cv_competences')}</h3>
                      <div className="form-row">
                        {[
                          { label: 'Langages', field: 'skillsLanguages', placeholder: 'JavaScript, Python, SQL' },
                          { label: 'Frameworks', field: 'skillsFrameworks', placeholder: 'React, Node.js, Django' },
                          { label: 'Outils', field: 'skillsTools', placeholder: 'Git, Docker, Figma' },
                          { label: 'Méthodo.', field: 'skillsMethods', placeholder: 'Agile, Scrum, CI/CD' },
                        ].map(({ label, field, placeholder }) => (
                          <div key={field} className="form-group">
                            <label className="form-label">{label}</label>
                            <input className="form-input" placeholder={placeholder} value={formData[field]} onChange={e => update(field, e.target.value)} />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="form-section">
                      <h3><i className="fas fa-globe"></i> {tr('cv_langues_cv')} & Certifications</h3>
                      <div className="form-row">
                        <div className="form-group"><label className="form-label">{tr('cv_langues_cv')}</label><input className="form-input" placeholder="Français (natif), Anglais (C1)" value={formData.languages} onChange={e => update('languages', e.target.value)} /></div>
                        <div className="form-group"><label className="form-label">Certifications</label><input className="form-input" placeholder="AWS Solutions Architect, TOEIC 900" value={formData.certifications} onChange={e => update('certifications', e.target.value)} /></div>
                      </div>
                    </div>

                    <div className="form-section">
                      <h3><i className="fas fa-project-diagram"></i> Projets clés <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 400 }}>(optionnel)</span></h3>
                      {(formData.projects || []).map((proj, idx) => (
                        <div key={proj.id} className="dynamic-item">
                          <div className="item-header">
                            <span className="item-number">Projet #{idx + 1}</span>
                            <button className="btn-remove" onClick={() => removeProj(proj.id)}><i className="fas fa-trash"></i> {tr('supprimer')}</button>
                          </div>
                          <div className="form-row">
                            <div className="form-group"><label className="form-label">Titre *</label><input className="form-input" placeholder="Nom du projet" value={proj.title} onChange={e => updateProj(proj.id, 'title', e.target.value)} /></div>
                            <div className="form-group"><label className="form-label">Technologies</label><input className="form-input" placeholder="React, Node.js" value={proj.technologies} onChange={e => updateProj(proj.id, 'technologies', e.target.value)} /></div>
                            <div className="form-group"><label className="form-label">Période</label><input className="form-input" placeholder="2023" value={proj.period} onChange={e => updateProj(proj.id, 'period', e.target.value)} /></div>
                          </div>
                          <div className="form-group"><label className="form-label">{tr('description')}</label><textarea className="form-textarea" rows={2} value={proj.description} onChange={e => updateProj(proj.id, 'description', e.target.value)} /></div>
                        </div>
                      ))}
                      <button className="btn btn-secondary btn-sm" onClick={addProj}><i className="fas fa-plus"></i> Ajouter un projet</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ÉTAPE 2 */}
            {step === 2 && (
              <div className="cv-container">
                <div className="cv-ai-panel">
                  <div className="card cv-ai-card">
                    <h3><i className="fas fa-magic" style={{ color: 'var(--accent-start)' }}></i> {tr('cv_ia_titre')}</h3>
                    <div className="form-group">
                      <label className="form-label"><i className="fas fa-briefcase"></i> {tr('cv_offre_emploi')}</label>
                      <textarea className="form-textarea" rows={8} value={jobOffer} onChange={e => setJobOffer(e.target.value)} placeholder="Collez ici le texte complet de l'offre d'emploi..." />
                    </div>
                    <button className="btn btn-primary cv-ai-btn" onClick={handleAdaptCV} disabled={aiLoading || !jobOffer.trim()}>
                      {aiLoading ? <><i className="fas fa-spinner fa-spin"></i> {tr('cv_analyse_ia')}...</> : <><i className="fas fa-magic"></i> {tr('cv_analyser')}</>}
                    </button>
                  </div>

                  {(formData.summary || coverLetter) && (
                    <div className="form-section cv-ai-results">
                      <h3><i className="fas fa-check-circle" style={{ color: '#10b981' }}></i> {tr('cv_analyse_ia')}</h3>
                      {formData.summary && (
                        <div className="cv-ai-result-block">
                          <label className="form-label"><i className="fas fa-align-left"></i> {tr('cv_resume')} adapté</label>
                          <textarea className="form-textarea" rows={4} value={formData.summary} onChange={e => update('summary', e.target.value)} />
                        </div>
                      )}
                      {coverLetter && (
                        <div className="cv-ai-result-block">
                          <label className="form-label"><i className="fas fa-envelope-open-text"></i> Lettre de motivation générée</label>
                          <div className="cv-cover-preview">
                            <pre className="cv-cover-text">{coverLetter.slice(0, 400)}...</pre>
                            <div className="cv-cover-actions">
                              <button className="btn btn-secondary btn-sm" onClick={() => setShowCoverLetter(true)}><i className="fas fa-eye"></i> Voir complète</button>
                              <button className="btn btn-primary btn-sm" onClick={generateCoverLetterPDF}><i className="fas fa-file-pdf"></i> {tr('telecharger_pdf')}</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!coverLetter && !aiLoading && (
                    <div className="cv-ai-hint">
                      <div className="cv-hint-steps">
                        {[
                          { icon: 'fa-paste', txt: `Colle l'${tr('cv_offre_emploi').toLowerCase()}` },
                          { icon: 'fa-magic', txt: `Clique sur ${tr('cv_analyser')}` },
                          { icon: 'fa-check', txt: 'Récupère CV + lettre' },
                        ].map((h, i) => (
                          <div key={i} className="cv-hint-step">
                            <div className="cv-hint-num">{i + 1}</div>
                            <i className={`fas ${h.icon}`}></i>
                            <span>{h.txt}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ÉTAPE 3 */}
            {step === 3 && (
              <div className="cv-container">
                <div className="cv-export-grid">
                  <div className="card cv-export-preview-card">
                    <h3><i className="fas fa-eye"></i> Aperçu rapide</h3>
                    <div className="cv-mini-preview" style={{ borderTopColor: formData.color }}>
                      <div className="cv-mp-name">{formData.name || 'Votre Nom'}</div>
                      <div className="cv-mp-title" style={{ color: formData.color }}>{formData.title || 'Titre professionnel'}</div>
                      <div className="cv-mp-contact">{[formData.email, formData.phone].filter(Boolean).join(' · ')}</div>
                      <div className="cv-mp-line" style={{ background: formData.color }}></div>
                      {formData.summary && <p className="cv-mp-summary">{formData.summary.slice(0, 150)}...</p>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                        <i className="fas fa-palette" style={{ marginRight: '0.4rem', color: formData.color }}></i>
                        {tr('cv_template')} : <strong>{TEMPLATES.find(t => t.id === formData.template)?.id}</strong>
                      </div>
                      <button className="btn btn-secondary" onClick={() => setShowPreview(true)} style={{ width: '100%' }}>
                        <i className="fas fa-expand"></i> Voir en plein écran
                      </button>
                    </div>
                  </div>

                  <div className="card cv-export-options">
                    <h3><i className="fas fa-download"></i> {tr('cv_telecharger')}</h3>
                    <div className="cv-export-btns">
                      <button className="cv-export-btn" onClick={generatePDF}><i className="fas fa-file-pdf"></i><strong>PDF</strong><span>Recommandé pour l'envoi</span></button>
                      <button className="cv-export-btn" onClick={generateWord}><i className="fas fa-file-word"></i><strong>Word (.docx)</strong><span>Modifiable sous Word</span></button>
                    </div>
                    {coverLetter && (
                      <>
                        <div className="cv-export-divider">Lettre de motivation</div>
                        <div className="cv-export-btns">
                          <button className="cv-export-btn" onClick={generateCoverLetterPDF}><i className="fas fa-envelope-open-text"></i><strong>Lettre PDF</strong><span>Générée par IA</span></button>
                          <button className="cv-export-btn cv-export-btn-preview" onClick={() => setShowCoverLetter(true)}><i className="fas fa-eye"></i><strong>Prévisualiser</strong><span>Voir la lettre</span></button>
                        </div>
                      </>
                    )}
                    {!coverLetter && (
                      <div className="cv-export-noletter">
                        <i className="fas fa-info-circle"></i> Pas encore de lettre. Retourne à l'étape 2 pour en générer une.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="cv-step-nav">
              {step > 1 && <button className="btn btn-secondary" onClick={() => setStep(s => s - 1)}><i className="fas fa-arrow-left"></i> {tr('cv_precedent')}</button>}
              {step < 3 && <button className="btn btn-primary" onClick={() => setStep(s => s + 1)} style={{ marginLeft: 'auto' }}>{step === 1 ? tr('cv_analyser') : tr('cv_etape3')}<i className="fas fa-arrow-right" style={{ marginLeft: '0.5rem' }}></i></button>}
            </div>

            {showPreview && <CVPreview formData={formData} onClose={() => setShowPreview(false)} onDownloadPDF={() => { generatePDF(); setShowPreview(false); }} onDownloadWord={generateWord} />}
            {showCoverLetter && coverLetter && <CoverLetterModal text={coverLetter} formData={formData} onClose={() => setShowCoverLetter(false)} onDownloadPDF={generateCoverLetterPDF} />}
          </>
        )}

        {/* Modale sauvegarder */}
        {showSaveModal && (
          <div className="cvp-overlay" onClick={() => setShowSaveModal(false)}>
            <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--border-radius-xl)', padding: '1.75rem', maxWidth: 420, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-primary)' }}>
                <i className="fas fa-save" style={{ color: 'var(--accent-start)' }}></i> Sauvegarder ce CV
              </h3>
              <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                <label className="form-label">Nom du CV *</label>
                <input
                  className="form-input"
                  placeholder="Ex: CV Développeur React 2026"
                  value={cvSaveName}
                  onChange={e => setCvSaveName(e.target.value)}
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleSaveCV()}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowSaveModal(false)}>Annuler</button>
                <button className="btn btn-primary btn-sm" onClick={handleSaveCV} disabled={!cvSaveName.trim()}>
                  <i className="fas fa-save"></i> Sauvegarder
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modale mes CV sauvegardés */}
        {showSavedCVs && (
          <div className="cvp-overlay" onClick={() => setShowSavedCVs(false)}>
            <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--border-radius-xl)', padding: '1.75rem', maxWidth: 560, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.2)', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-primary)' }}>
                  <i className="fas fa-folder-open" style={{ color: 'var(--accent-start)' }}></i> Mes CV sauvegardés
                </h3>
                <button className="cvp-close" onClick={() => setShowSavedCVs(false)}><i className="fas fa-times"></i></button>
              </div>

              {savedCVs.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem' }}>Aucun CV sauvegardé</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {savedCVs.map(cv => (
                    <div key={cv.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'var(--bg-subtle)', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--border)' }}>
                      <div style={{ width: 40, height: 40, borderRadius: 'var(--border-radius-md)', background: 'linear-gradient(135deg, var(--accent-start), var(--accent-end))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
                        <i className="fas fa-file-alt"></i>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cv.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          {new Date(cv.savedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                          {cv.data?.name && ` · ${cv.data.name}`}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => handleLoadCV(cv)}>
                          <i className="fas fa-folder-open"></i> Charger
                        </button>
                        <button className="btn btn-sm" style={{ background: '#fee2e215', color: '#ef4444', border: '1px solid #ef444430' }} onClick={() => handleDeleteSavedCV(cv.id)}>
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default CV;